import React, { useState, useEffect } from "react";
import { Box, Typography, Grid } from "@mui/joy";
import { AnimatePresence, motion } from "framer-motion";
import { Calculator } from "lucide-react";
import { EstimatingSkeleton } from "../skeletons";
import { useProject, useNotification } from "../../../../hooks";
import { useInspectionAttributes } from "../../../../hooks/query";

import SiteSetup from "./SiteSetup";
import FeesAndIncidentals from "./FeesAndIncidentals";
import FinancialSettings from "./FinancialSettings";
import BidPricing from "./BidPricing";
import ProjectNotes from "./ProjectNotes";
import { DEFAULT_ESTIMATING_VALUES, EST_NUMERIC_KEYS, PROJECT_NOTES_ATTRIBUTE } from "./estimatingDefaults";
import { findAttributeByName, getAttributeValue } from "../../../../utils/inspectionAttributes";

interface EstimatingProps {
    measurements: any[];
}

export default function Estimating({ measurements }: EstimatingProps) {
    const { project } = useProject();
    const { showError, showSuccess } = useNotification();
    const { useListByProjectId, useUpdateSingleAttribute } = useInspectionAttributes();
    const listAttributesSvc = useListByProjectId();
    const updateAttributeSvc = useUpdateSingleAttribute();

    const [isLoading, setIsLoading] = useState(true);
    const [values, setValues] = useState<Record<string, any>>(DEFAULT_ESTIMATING_VALUES);
    const [projectNotesId, setProjectNotesId] = useState<string | undefined>(undefined);

    useEffect(() => {
        const fetchAttributes = async () => {
            if (!project?.id) return;
            try {
                const data = await listAttributesSvc.mutateAsync({ ProjectId: project.id });

                const dbValues: Record<string, any> = {};
                const notesAttr = findAttributeByName(data, PROJECT_NOTES_ATTRIBUTE);

                data.forEach((attr: any) => {
                    if (attr.AttributeName === PROJECT_NOTES_ATTRIBUTE) return;
                    if (!attr.AttributeName?.startsWith("EST_")) return;
                    let val: any = attr.AttributeValue;
                    if (EST_NUMERIC_KEYS.has(attr.AttributeName)) val = Number(val);
                    dbValues[attr.AttributeName] = val;
                });

                dbValues[PROJECT_NOTES_ATTRIBUTE] = getAttributeValue(data, PROJECT_NOTES_ATTRIBUTE);
                setProjectNotesId(notesAttr?.Id);

                setValues((prev) => ({ ...prev, ...dbValues }));
                setIsLoading(false);
            } catch (error) {
                console.error("Error fetching estimating attributes:", error);
                setIsLoading(false);
            }
        };

        fetchAttributes();
    }, [project?.id]);

    const handleUpdate = async (key: string, value: any) => {
        if (!project?.id) return;

        const oldValue = values[key];
        setValues((prev) => ({ ...prev, [key]: value }));

        try {
            await updateAttributeSvc.mutateAsync({
                ProjectId: project.id,
                AttributeName: key,
                Value: String(value),
            });
        } catch (error) {
            console.error("Error updating attribute:", error);
            showError(`Failed to save ${key.replace("EST_", "").replace(/_/g, " ").toLowerCase()}`);
            setValues((prev) => ({ ...prev, [key]: oldValue }));
        }
    };

    const handleSaveProjectNotes = async (value: string) => {
        if (!project?.id) return;

        try {
            const response = await updateAttributeSvc.mutateAsync({
                Id: projectNotesId,
                ProjectId: project.id,
                AttributeName: PROJECT_NOTES_ATTRIBUTE,
                Value: String(value),
            });
            if (response?.Id) {
                setProjectNotesId(response.Id);
            }
            setValues((prev) => ({ ...prev, [PROJECT_NOTES_ATTRIBUTE]: value }));
            showSuccess("Project notes saved");
        } catch (error) {
            console.error("Error saving project notes:", error);
            showError("Failed to save project notes");
            throw error;
        }
    };

    return (
        <AnimatePresence mode="popLayout">
            {isLoading ? (
                <motion.div
                    key="skeleton"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <EstimatingSkeleton />
                </motion.div>
            ) : (
                <motion.div
                    key="content"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <Box>
                        <Box mb={2}>
                            <Typography level="title-md" startDecorator={<Calculator size={16} />}>
                                Estimating Configuration
                            </Typography>
                            <Typography level="body-sm" color="neutral">
                                Billing rules, fees, and margin settings — applied when the contract PDF is generated.
                            </Typography>
                        </Box>

                        <Grid container spacing={2}>
                            <Grid xs={12} md={7}>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                    <SiteSetup values={values} onChange={handleUpdate} />
                                    <BidPricing values={values} onChange={handleUpdate} />
                                    <ProjectNotes
                                        value={String(values[PROJECT_NOTES_ATTRIBUTE] || "")}
                                        onSave={handleSaveProjectNotes}
                                    />
                                </Box>
                            </Grid>

                            <Grid xs={12} md={5}>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                    <FinancialSettings values={values} onChange={handleUpdate} />
                                    <FeesAndIncidentals values={values} onChange={handleUpdate} />
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
